import { useState, useEffect, useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import InputAdornment from '@mui/material/InputAdornment';
import Fade from '@mui/material/Fade';
import Slide from '@mui/material/Slide';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import {
  Close,
  Business,
  Email,
  LocationOn,
  Category,
  Schedule,
  CalendarToday,
  CheckCircle,
  Edit,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectAllCompanies,
  addCompanyStatus,
  addCompanyError,
  editCompanyStatus,
  editCompanyError,
} from '../store/company.selectors';
import {
  selectAllStates,
  selectAllCompanyTypes,
} from '../../../utils/common/store/common.selectors';
import { fetchAllStates, fetchAllCompanyTypes } from '../../../utils/common/store/common.actions';
import {
  addCompany,
  fetchAllCompanies,
  editCompany,
  resetSnackbarDetails,
} from '../store/company.actions';
import { selectedCompanyDetails } from '../store/company.selectors';
import PropTypes from 'prop-types';
import {
  StyledModal,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  StyledCloseButton,
  ModalContent,
  StyledTextField,
  StyledFormControl,
  StyledSelect,
  PrimaryButton,
  HelperText,
  FormSection,
} from '../../../utils/common/styles/commonStyles';

const AddCompanyModal = ({ open, handleClose, mode = 'add' }) => {
  const dispatch = useDispatch();
  const states = useSelector(selectAllStates);
  const companyTypes = useSelector(selectAllCompanyTypes);
  const companies = useSelector(selectAllCompanies);
  const newCompStatus = useSelector(addCompanyStatus);
  const newCompError = useSelector(addCompanyError);
  const editSelectedCompany = useSelector(selectedCompanyDetails);
  const editCompStatus = useSelector(editCompanyStatus);
  const editCompError = useSelector(editCompanyError);

  const initialFormData = {
    companyName: '',
    billingEmail: '',
    contractedToCompanyID: '',
    stateID: '',
    companyTypeID: '',
    duration: '',
    subscriptionEndDate: null,
  };
  const DetailCard = ({ icon, title, value }) => (
    <Paper
      elevation={3}
      sx={{
        p: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        borderRadius: 2,
        background: 'linear-gradient(135deg, #FFFFFF 0%, #F3F4F6 100%)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      }}
    >
      <Box sx={{ color: '#667eea' }}>{icon}</Box>
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          {value}
        </Typography>
      </Box>
    </Paper>
  );
  DetailCard.propTypes = {
    icon: PropTypes.element.isRequired,
    title: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  };
  const TabPanel = ({ children, value, index, ...other }) => (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`company-tabpanel-${index}`}
      aria-labelledby={`company-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );

  TabPanel.propTypes = {
    children: PropTypes.node,
    value: PropTypes.number.isRequired,
    index: PropTypes.number.isRequired,
  };

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const [formData, setFormData] = useState(initialFormData);
  const [isEdit, setIsEdit] = useState(false);
  const readOnly = mode === 'view';
  const [isLoading, setIsLoading] = useState(false);

  const hasCompanyChanges = useMemo(() => {
    if (mode !== 'edit' || !editSelectedCompany) return true;
    return (
      formData.companyName !== editSelectedCompany.companyName ||
      formData.billingEmail !== editSelectedCompany.billingEmail ||
      formData.contractedToCompanyID !== editSelectedCompany.contractedToCompanyID ||
      formData.stateID !== editSelectedCompany.stateID ||
      formData.companyTypeID !== editSelectedCompany.companyTypeID ||
      (formData.subscriptionEndDate || null) !==
        (editSelectedCompany.subscriptionEndDate?.split('T')[0] || null)
    );
  }, [mode, editSelectedCompany, formData]);

  const durationOptions = {
    1: { label: '15 days' },
    2: { label: '3 months' },
    3: { label: '6 months' },
    4: { label: '1 year' },
  };

  useEffect(() => {
    if (open) {
      dispatch(fetchAllStates());
      dispatch(fetchAllCompanyTypes());
    } else {
      setFormData(initialFormData);
      setIsEdit(false);
      setIsLoading(false);
    }
  }, [open]);

  useEffect(() => {
    if (newCompStatus === 'success' || editCompStatus === 'success') {
      setSnackbar({
        open: true,
        message: isEdit ? 'Company updated successfully!' : 'Company created successfully!',
        severity: 'success',
      });
      dispatch(fetchAllCompanies());
      handleClose();
      setFormData(initialFormData);
      setIsLoading(false);
    } else if (
      (newCompStatus === 'fail' && newCompError) ||
      (editCompStatus === 'fail' && editCompError)
    ) {
      setSnackbar({
        open: true,
        message: isEdit ? editCompError : newCompError,
        severity: 'error',
      });
      setIsLoading(false);
    }
    dispatch(resetSnackbarDetails());
  }, [newCompStatus, newCompError, editCompStatus, editCompError]);

  useEffect(() => {
    if (open && editSelectedCompany) {
      setIsEdit(mode === 'edit');
      setFormData({
        companyName: editSelectedCompany.companyName,
        billingEmail: editSelectedCompany.billingEmail,
        contractedToCompanyID: editSelectedCompany.contractedToCompanyID,
        stateID: editSelectedCompany.stateID,
        companyTypeID: editSelectedCompany.companyTypeID,
        duration: '',
        subscriptionEndDate: editSelectedCompany.subscriptionEndDate?.split('T')[0] || null,
      });
    }
  }, [open, editSelectedCompany, mode]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'duration') {
      const today = new Date();
      let endDate = new Date(today);

      switch (value) {
        case 1:
          endDate.setDate(today.getDate() + 15);
          break;
        case 2:
          endDate.setMonth(today.getMonth() + 3);
          break;
        case 3:
          endDate.setMonth(today.getMonth() + 6);
          break;
        case 4:
          endDate.setFullYear(today.getFullYear() + 1);
          break;
        default:
          break;
      }

      const formattedDate = endDate.toISOString().split('T')[0];
      setFormData((prev) => ({
        ...prev,
        duration: value,
        subscriptionEndDate: formattedDate,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = () => {
    setIsLoading(true);
    if (isEdit) dispatch(editCompany({ ...formData, id: editSelectedCompany.id }));
    else dispatch(addCompany(formData));
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false, message: '' }));
  };

  const isFormValid =
    formData.companyName &&
    formData.billingEmail &&
    formData.stateID &&
    formData.companyTypeID &&
    formData.contractedToCompanyID !== '' &&
    formData.subscriptionEndDate;

  return (
    <>
      <StyledModal open={open} onClose={handleClose}>
        <Fade in={open} timeout={300}>
          <ModalContainer>
            <ModalHeader>
              <ModalTitle>
                {mode === 'edit' ? <Edit /> : <Business />}
                {mode === 'view'
                  ? 'Company Details'
                  : mode === 'edit'
                    ? 'Edit Company'
                    : 'Add New Company'}
              </ModalTitle>
              <StyledCloseButton
                onClick={() => {
                  setFormData(initialFormData);
                  handleClose();
                }}
                size="small"
              >
                <Close />
              </StyledCloseButton>
            </ModalHeader>

            <ModalContent>
              {readOnly ? (
                <>
                  <TabPanel>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <DetailCard
                          icon={<Business fontSize="small" />}
                          title="Company Name"
                          value={formData.companyName || '–'}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <DetailCard
                          icon={<Email fontSize="small" />}
                          title="Billing Email"
                          value={formData.billingEmail || '–'}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <DetailCard
                          icon={<Business fontSize="small" />}
                          title="Contracted To"
                          value={
                            companies?.find((c) => c.id === formData.contractedToCompanyID)
                              ?.companyName || 'Self'
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <DetailCard
                          icon={<LocationOn fontSize="small" />}
                          title="State"
                          value={states?.find((s) => s.id === formData.stateID)?.name || '–'}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <DetailCard
                          icon={<Category fontSize="small" />}
                          title="Company Type"
                          value={
                            companyTypes?.find((t) => t.id === formData.companyTypeID)?.name || '–'
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} sx={{ alignContent: 'center' }}>
                        <DetailCard
                          icon={<CalendarToday fontSize="small" />}
                          title="Subscription End Date"
                          value={formData.subscriptionEndDate || '–'}
                        />
                      </Grid>
                    </Grid>
                  </TabPanel>
                </>
              ) : (
                <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <FormSection>
                    <StyledTextField
                      required
                      label="Company Name"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      disabled={readOnly}
                      placeholder="Enter company name"
                      fullWidth
                      size="small"
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <Business color="action" fontSize="small" />
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                  </FormSection>

                  <FormSection>
                    <StyledTextField
                      required
                      label="Billing Email"
                      name="billingEmail"
                      type="email"
                      value={formData.billingEmail}
                      onChange={handleChange}
                      disabled={readOnly}
                      placeholder="billing@company.com"
                      fullWidth
                      size="small"
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <Email color="action" fontSize="small" />
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                  </FormSection>

                  <FormSection>
                    <StyledFormControl required fullWidth size="small">
                      <InputLabel>Contracted to Company</InputLabel>
                      <StyledSelect
                        name="contractedToCompanyID"
                        value={formData.contractedToCompanyID}
                        onChange={handleChange}
                        disabled={readOnly}
                        label="Contracted to Company"
                        startAdornment={
                          <InputAdornment position="start" sx={{ ml: 1 }}>
                            <Business color="action" fontSize="small" />
                          </InputAdornment>
                        }
                      >
                        <MenuItem value={0}>Self</MenuItem>
                        {companies?.map((company) => (
                          <MenuItem value={company.id} key={company.id}>
                            {company.companyName}
                          </MenuItem>
                        ))}
                      </StyledSelect>
                    </StyledFormControl>

                    <HelperText>
                      <CheckCircle fontSize="small" />
                      {`Select "Self" if this company is the purchasing company`}
                    </HelperText>
                  </FormSection>

                  <Box display="flex" gap={2}>
                    <StyledFormControl required sx={{ flex: 1 }} size="small">
                      <InputLabel>State</InputLabel>
                      <StyledSelect
                        name="stateID"
                        value={formData.stateID}
                        onChange={handleChange}
                        disabled={readOnly}
                        label="State"
                        startAdornment={
                          <InputAdornment position="start" sx={{ ml: 1 }}>
                            <LocationOn color="action" fontSize="small" />
                          </InputAdornment>
                        }
                      >
                        {states?.map((state) => (
                          <MenuItem key={state.description} value={state.id}>
                            {state.name}
                          </MenuItem>
                        ))}
                      </StyledSelect>
                    </StyledFormControl>

                    <StyledFormControl required sx={{ flex: 1 }} size="small">
                      <InputLabel>Company Type</InputLabel>
                      <StyledSelect
                        name="companyTypeID"
                        value={formData.companyTypeID}
                        onChange={handleChange}
                        disabled={readOnly}
                        label="Company Type"
                        startAdornment={
                          <InputAdornment position="start" sx={{ ml: 1 }}>
                            <Category color="action" fontSize="small" />
                          </InputAdornment>
                        }
                      >
                        {companyTypes?.map((type) => (
                          <MenuItem value={type.id} key={type.description}>
                            {type.name}
                          </MenuItem>
                        ))}
                      </StyledSelect>
                    </StyledFormControl>
                  </Box>

                  <Divider sx={{ my: 1, opacity: 0.5 }} />

                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    Subscription Details
                  </Typography>

                  <Box display="flex" gap={2}>
                    <StyledFormControl required sx={{ flex: 1 }} size="small">
                      <InputLabel>Duration</InputLabel>
                      <StyledSelect
                        name="duration"
                        value={formData.duration}
                        onChange={handleChange}
                        disabled={readOnly}
                        label="Duration"
                        startAdornment={
                          <InputAdornment position="start" sx={{ ml: 1 }}>
                            <Schedule color="action" fontSize="small" />
                          </InputAdornment>
                        }
                      >
                        {Object.entries(durationOptions).map(([value, { label }]) => (
                          <MenuItem value={parseInt(value)} key={value}>
                            <Box display="flex" alignItems="center" gap={1}>
                              {label}
                            </Box>
                          </MenuItem>
                        ))}
                      </StyledSelect>
                    </StyledFormControl>

                    <StyledTextField
                      required
                      disabled
                      label="Subscription End Date"
                      name="subscriptionEndDate"
                      type="date"
                      value={formData.subscriptionEndDate || ''}
                      slotProps={{
                        inputLabel: { shrink: true },
                        input: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <CalendarToday color="action" fontSize="small" />
                            </InputAdornment>
                          ),
                        },
                      }}
                      sx={{ flex: 1 }}
                      size="small"
                    />
                  </Box>

                  {!readOnly && (
                    <Box display="flex" justifyContent="flex-end" mt={3}>
                      <PrimaryButton
                        onClick={handleSubmit}
                        disabled={
                          !isFormValid || isLoading || (mode === 'edit' && !hasCompanyChanges)
                        }
                        sx={{ minWidth: 160 }}
                      >
                        {isLoading ? (
                          <Box display="flex" alignItems="center" gap={1}>
                            <CircularProgress size={18} color="inherit" />
                            <span>{isEdit ? 'Updating...' : 'Creating...'}</span>
                          </Box>
                        ) : (
                          <>{isEdit ? 'Update Company' : 'Create Company'}</>
                        )}
                      </PrimaryButton>
                    </Box>
                  )}
                </Box>
              )}
            </ModalContent>
          </ModalContainer>
        </Fade>
      </StyledModal>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        TransitionComponent={Slide}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            width: '100%',
            fontWeight: 500,
            '& .MuiAlert-icon': {
              fontSize: '1.2rem',
            },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

AddCompanyModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(['add', 'edit', 'view']),
};

export default AddCompanyModal;
